import { gql, useLazyQuery } from "@apollo/client";
import { coordinateToMapPoint, MapPoint } from "@caredna-marlin/map-wrapper";
import { useEffect, useState } from "react";

export type MapPatientInfo = {
  fullName: string;
  location: MapPoint | undefined;
};

const SESSION_KEY = 'selected-map-id';

function setStoredPatientId(patientId: number): void {
  sessionStorage.setItem(SESSION_KEY, patientId.toString());
}

function getStoredPatientId(): number | null {
  const data = sessionStorage.getItem(SESSION_KEY);
  if (!data) return null;

  try {
    return parseInt(data);
  } catch (e) {
    console.warn('Failed to parse selected patient from sessionStorage', e);
  }

  return null;
}

function clearStoredPatientId(): void {
  sessionStorage.removeItem(SESSION_KEY);
}

export function useMapPatientInfo() {
  const [patientId, setPatientId] = useState(() => getStoredPatientId());
  const [mapPatientInfo, setMapPatientInfo] = useState<MapPatientInfo | null>(null);
  const [loadPatientData, { loading }] = useLazyQuery(PATIENT_LOCATION_QUERY, {
    onCompleted: (data) => {
      if (data?.patientSingle) {
        const { firstName, lastName, addressCoordinates } = data.patientSingle;
        const fullName = `${firstName} ${lastName}`;
        const location = addressCoordinates ? coordinateToMapPoint(addressCoordinates) || undefined : undefined
        setMapPatientInfo({ fullName, location });
      }
    }
  })

  useEffect(() => {
    if(patientId) {
      loadPatientData({ variables: { patientId: patientId } })
    }
    else {
      setMapPatientInfo(null);
    }
  }, [patientId])

  const setMapPatientId = (patientId: number | null) => {
    setPatientId(patientId);
    if (patientId) {
      setStoredPatientId(patientId)
    } else {
      clearStoredPatientId();
    }
  };

  return { mapPatientInfo, loadingPatientInfo: loading, setMapPatientId };
}

const PATIENT_LOCATION_QUERY = gql`
  query PatientLocation($patientId: Int!)
  @permission(requires: useWebApp)
  {
    patientSingle(where: { id: { eq: $patientId } }) {
      firstName
      lastName
      addressCoordinates
    }
  }
`;